import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TabsContent } from '../ui/tabs';
import { Brain, Settings as SettingsIcon } from 'lucide-react';

export default function AdminAIConfigTab({
  aiConfig,
  setAiConfig,
  handleUpdateAIConfig
}) {
  return (
    <TabsContent value="ai-config" className="space-y-6">
      <div className="flex items-start gap-4">
        <Brain className="w-5 h-5 text-blue-500 mt-0.5" />
        <div>
          <h2 className="text-xl font-semibold text-foreground">Default AI Configuration</h2>
          <p className="text-sm text-muted-foreground mt-1">
            These settings are used as <strong>defaults</strong> when a client doesn't have custom AI settings configured.
            Client-specific settings (in the Clients tab) will override these defaults.
          </p>
        </div>
      </div>

      {aiConfig && (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle>System Default Settings</CardTitle>
            <CardDescription>
              Applied when client has no custom AI configuration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateAIConfig} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Default Model</Label>
                  <Input
                    value={aiConfig.default_model}
                    onChange={(e) => setAiConfig({ ...aiConfig, default_model: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Allowed Models (comma-separated)</Label>
                  <Input
                    value={aiConfig.allowed_models.join(', ')}
                    onChange={(e) => setAiConfig({ ...aiConfig, allowed_models: e.target.value.split(',').map(m => m.trim()) })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Temperature Min</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={aiConfig.temperature_min}
                    onChange={(e) => setAiConfig({ ...aiConfig, temperature_min: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Temperature Max</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={aiConfig.temperature_max}
                    onChange={(e) => setAiConfig({ ...aiConfig, temperature_max: parseFloat(e.target.value) })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Default Distribution</Label>
                  <Select
                    value={aiConfig.default_distribution}
                    onValueChange={(value) => setAiConfig({ ...aiConfig, default_distribution: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="uniform">Uniform</SelectItem>
                      <SelectItem value="truncated_normal">Truncated Normal</SelectItem>
                      <SelectItem value="beta">Beta</SelectItem>
                      <SelectItem value="bimodal">Bimodal</SelectItem>
                      <SelectItem value="triangular">Triangular</SelectItem>
                      <SelectItem value="skewed_normal">Skewed Normal</SelectItem>
                      <SelectItem value="log_normal">Log-Normal</SelectItem>
                      <SelectItem value="exponential">Exponential</SelectItem>
                      <SelectItem value="discrete_step">Discrete Step</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Allowed Distributions (comma-separated)</Label>
                  <Input
                    value={aiConfig.allowed_distributions.join(', ')}
                    onChange={(e) => setAiConfig({ ...aiConfig, allowed_distributions: e.target.value.split(',').map(m => m.trim()) })}
                  />
                </div>
              </div>

              <Button type="submit" className="bg-primary hover:bg-primary/90">
                <SettingsIcon className="w-4 h-4 mr-2" />
                Save Configuration
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </TabsContent>
  );
}